import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Monitor, ArrowLeft } from 'lucide-react';
import { validateMeetingId, validateParticipantName } from '@/utils';
import { getSavedUserName } from '@/utils/storage';

export const JoinMeeting: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const meetingIdFromUrl = searchParams.get('id') || '';

  const [meetingId, setMeetingId] = useState(meetingIdFromUrl);
  const [participantName, setParticipantName] = useState(getSavedUserName() || '');
  const [errors, setErrors] = useState({ meetingId: '', name: '' });

  const handleJoin = () => {
    const newErrors = { meetingId: '', name: '' };

    if (!meetingId.trim()) {
      newErrors.meetingId = 'Meeting ID is required';
    } else if (!validateMeetingId(meetingId)) {
      newErrors.meetingId = 'Invalid meeting ID format';
    }

    if (!participantName.trim()) {
      newErrors.name = 'Your name is required';
    } else if (!validateParticipantName(participantName)) {
      newErrors.name = 'Name must be between 1 and 50 characters';
    }

    setErrors(newErrors);

    if (!newErrors.meetingId && !newErrors.name) {
      navigate(`/meeting/${meetingId}?name=${encodeURIComponent(participantName)}`);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl flex items-center justify-center">
              <Monitor className="text-white" size={24} />
            </div>
            <span className="text-3xl font-bold text-white">
              Miiglu Meet
            </span>
          </div>
          <p className="text-neutral-400">Join a meeting in progress</p>
        </div>

        {/* Form Card */}
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="space-y-5">
            {/* Meeting ID Input */}
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Meeting ID
              </label>
              <input
                type="text"
                placeholder="Enter meeting ID"
                value={meetingId}
                onChange={(e) => setMeetingId(e.target.value)}
                className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 transition-all ${
                  errors.meetingId
                    ? 'border-red-500/50 focus:ring-red-500/50'
                    : 'border-white/10 focus:ring-primary-400/50 focus:border-primary-400/50'
                }`}
              />
              {errors.meetingId && (
                <p className="mt-2 text-sm text-red-400">{errors.meetingId}</p>
              )}
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-neutral-300 mb-2">
                Your Name
              </label>
              <input
                type="text"
                placeholder="Enter your name"
                value={participantName}
                onChange={(e) => setParticipantName(e.target.value)}
                className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 transition-all ${
                  errors.name
                    ? 'border-red-500/50 focus:ring-red-500/50'
                    : 'border-white/10 focus:ring-primary-400/50 focus:border-primary-400/50'
                }`}
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-400">{errors.name}</p>
              )}
            </div>

            {/* Join Button */}
            <button
              onClick={handleJoin}
              className="w-full px-6 py-3.5 bg-gradient-to-r from-primary-400 to-primary-500 hover:from-primary-500 hover:to-primary-600 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-all duration-200"
            >
              Join Meeting
            </button>

            {/* Back Button */}
            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-neutral-300 font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-6 flex items-center justify-center gap-6 text-sm text-neutral-500">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-accent-400 rounded-full" />
            <span>Secure</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-primary-400 rounded-full" />
            <span>Instant join</span>
          </div>
        </div>
      </div>
    </div>
  );
};